import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import Logo from '@components/ui/Logo';
import { useTheme } from '@context/ThemeContext';
import { Sun, Moon } from 'lucide-react';

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-bold text-surface-900 dark:text-white mb-3">{title}</h2>
    <div className="space-y-3 text-sm leading-relaxed text-surface-600 dark:text-surface-400">
      {children}
    </div>
  </section>
);

const PrivacyPolicyPage = () => {
  const { dark, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-950">
      <header className="sticky top-0 z-40 glass-header">
        <div className="w-full max-w-4xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
          <Link to="/" className="flex items-center gap-2 text-surface-500 hover:text-surface-800 dark:hover:text-white transition-colors">
            <ArrowLeft size={18} />
            <Logo size="sm" />
          </Link>
          <button onClick={toggleTheme} className="theme-toggle" aria-label="Toggle dark mode">
            {dark ? <Sun size={18} strokeWidth={2} /> : <Moon size={18} strokeWidth={2} />}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-20">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 dark:bg-brand-500/10">
            <Shield size={20} className="text-brand-500" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-surface-900 dark:text-white">
            Privacy Policy
          </h1>
        </div>
        <p className="text-xs text-surface-400 dark:text-surface-500 mb-10">
          Last updated: March 12, 2026
        </p>

        <div className="rounded-2xl border border-surface-200/60 bg-white p-6 sm:p-8 shadow-sm dark:border-surface-800/60 dark:bg-surface-900">
          <Section title="1. Introduction">
            <p>
              Welcome to Yaarvi. Your privacy is important to us. This Privacy Policy explains how
              we collect, use, disclose, and safeguard your information when you use our web platform
              at yaarvi.com (the &ldquo;Service&rdquo;). By using Yaarvi, you agree to the
              collection and use of information in accordance with this policy.
            </p>
          </Section>

          <Section title="2. Information We Collect">
            <p>When you sign up or log in using Google OAuth, we collect the following information from your Google account:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong className="text-surface-800 dark:text-surface-200">Name</strong> &mdash; your display name as provided by Google.</li>
              <li><strong className="text-surface-800 dark:text-surface-200">Email address</strong> &mdash; used for account identification and communication.</li>
              <li><strong className="text-surface-800 dark:text-surface-200">Profile picture</strong> &mdash; displayed on your Yaarvi profile.</li>
            </ul>
            <p>We may also collect:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong className="text-surface-800 dark:text-surface-200">Location data</strong> &mdash; approximate location (with your permission) to show nearby vibes and connect you with people in your area.</li>
              <li><strong className="text-surface-800 dark:text-surface-200">Usage data</strong> &mdash; information about how you interact with the Service, such as pages visited, vibes created or joined, and session duration.</li>
              <li><strong className="text-surface-800 dark:text-surface-200">Device information</strong> &mdash; browser type, operating system, and device identifiers for analytics and security purposes.</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Information">
            <p>We use the collected information for the following purposes:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>To authenticate your identity and manage your account.</li>
              <li>To display your profile to other users on the platform.</li>
              <li>To connect you with nearby users who share similar vibe interests.</li>
              <li>To improve, personalize, and enhance the user experience.</li>
              <li>To send important service-related notifications (e.g., vibe updates).</li>
              <li>To monitor and analyze usage trends and improve our Service.</li>
              <li>To detect, prevent, and address technical issues or abuse.</li>
            </ul>
          </Section>

          <Section title="4. Data Sharing &amp; Disclosure">
            <p>
              <strong className="text-surface-800 dark:text-surface-200">We do not sell, trade, or rent your personal data to third parties.</strong>
            </p>
            <p>We may share your information only in the following circumstances:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong className="text-surface-800 dark:text-surface-200">With other users</strong> &mdash; your name, profile picture, and vibe information are visible to other Yaarvi users as part of the platform&apos;s core functionality.</li>
              <li><strong className="text-surface-800 dark:text-surface-200">Service providers</strong> &mdash; we may employ trusted third-party services (e.g., hosting, analytics) that have access to your data solely to perform tasks on our behalf.</li>
              <li><strong className="text-surface-800 dark:text-surface-200">Legal requirements</strong> &mdash; we may disclose your information if required by law or in response to valid legal processes.</li>
            </ul>
          </Section>

          <Section title="5. Cookies &amp; Analytics">
            <p>
              Yaarvi may use cookies and similar tracking technologies to maintain your session,
              remember your preferences (such as dark mode), and gather aggregate analytics data.
              These cookies are essential for the functioning of the Service. We may use third-party
              analytics tools (such as Google Analytics) to understand usage patterns. These tools
              collect anonymized data and do not personally identify you.
            </p>
          </Section>

          <Section title="6. Data Security">
            <p>
              We implement industry-standard security measures to protect your personal data,
              including encrypted connections (HTTPS), secure authentication via Google OAuth 2.0,
              and access controls on our servers. However, no method of transmission over the Internet
              is 100% secure, and we cannot guarantee absolute security.
            </p>
          </Section>

          <Section title="7. Data Retention &amp; Deletion">
            <p>
              We retain your personal data for as long as your account is active or as needed to
              provide you the Service. You may request deletion of your account and all associated
              personal data at any time. Please visit our{' '}
              <Link to="/delete-account" className="text-brand-500 hover:text-brand-600 font-medium underline underline-offset-2">
                Data Deletion page
              </Link>{' '}
              for instructions.
            </p>
          </Section>

          <Section title="8. Children&rsquo;s Privacy">
            <p>
              Yaarvi is not intended for use by individuals under the age of 13. We do not
              knowingly collect personal information from children under 13. If we become aware that
              we have collected such data, we will take steps to delete it promptly.
            </p>
          </Section>

          <Section title="9. Your Rights">
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Access and receive a copy of your personal data.</li>
              <li>Request correction of inaccurate data.</li>
              <li>Request deletion of your personal data.</li>
              <li>Object to or restrict processing of your data.</li>
              <li>Withdraw consent at any time (where processing is based on consent).</li>
            </ul>
            <p>To exercise any of these rights, please contact us at the email below.</p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any
              significant changes by posting the new policy on this page and updating the
              &ldquo;Last updated&rdquo; date. Your continued use of the Service after changes
              constitutes acceptance of the updated policy.
            </p>
          </Section>

          <Section title="11. Contact Us">
            <p>
              If you have any questions or concerns about this Privacy Policy, please contact us at:
            </p>
            <p className="mt-2">
              <a href="mailto:support@yaarvi.com" className="text-brand-500 hover:text-brand-600 font-semibold underline underline-offset-2">
                support@yaarvi.com
              </a>
            </p>
          </Section>
        </div>

        <div className="mt-8 flex flex-wrap gap-4 text-xs text-surface-400 dark:text-surface-500">
          <Link to="/terms" className="hover:text-brand-500 transition-colors underline underline-offset-2">Terms of Service</Link>
          <Link to="/delete-account" className="hover:text-brand-500 transition-colors underline underline-offset-2">Delete Account</Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicyPage;
